import { ExternalLink, Paperclip, X } from "lucide-react";
import { useNotify } from "ra-core";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { openAccountingFile, uploadAccountingFile } from "./useAccountingData";

export type StoredFile = { path: string; name: string } | null;

/** Invoice or receipt: upload (PDF or photo, also from the phone camera), open, remove. */
export const AccountingFileField = ({
  label,
  value,
  folder,
  day,
  onChange,
  uploadLabel = "Subir PDF o foto de la factura",
}: {
  label: string;
  uploadLabel?: string;
  value: StoredFile;
  folder: "expenses" | "incomes";
  day: string;
  onChange: (file: StoredFile) => void;
}) => {
  const notify = useNotify();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadAccountingFile(file, folder, day));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error al subir", {
        type: "error",
      });
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  const open = async () => {
    if (!value) return;
    try {
      await openAccountingFile(value.path);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label ? <Label>{label}</Label> : null}
      <input
        ref={input}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(event) => upload(event.target.files?.[0])}
      />
      {value ? (
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
          <button
            type="button"
            onClick={open}
            className="min-w-0 flex-1 truncate text-left hover:underline"
            title="Abrir"
          >
            {value.name}
          </button>
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Quitar archivo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => input.current?.click()}
          className="justify-start border-dashed"
        >
          <Paperclip className="h-4 w-4" />
          {uploading ? "Subiendo…" : uploadLabel}
        </Button>
      )}
    </div>
  );
};
